import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  History,
  ArrowLeft,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  AlertCircle,
  Download,
  ExternalLink,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency, cn } from '../../lib/utils';
import { useCryptoHistory, usePrimaryVirtualAccount, CryptoOperation } from '../../hooks/useCrypto';

const TYPE_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'PURCHASE', label: 'Compras' },
  { value: 'SALE', label: 'Vendas' },
  { value: 'DEPOSIT', label: 'Depósitos DeFi' },
  { value: 'WITHDRAW', label: 'Resgates DeFi' },
  { value: 'TRANSFER_IN', label: 'Recebimentos' },
  { value: 'TRANSFER_OUT', label: 'Envios' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'COMPLETED', label: 'Concluídas' },
  { value: 'PENDING', label: 'Pendentes' },
  { value: 'FAILED', label: 'Falhas' },
];

const PERIOD_OPTIONS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: '365', label: 'Último ano' },
  { value: '', label: 'Todo o período' },
];

export function BankingCryptoHistory() {
  const navigate = useNavigate();
  const { hasCategory } = useAuthStore();
  
  // Get primary virtual account ID from banking API
  const { data: primaryAccount, isLoading: accountLoading } = usePrimaryVirtualAccount();
  const virtualAccountId = primaryAccount?.id || '';

  const [filters, setFilters] = useState<{
    type?: string;
    status?: string;
    period?: string;
    search?: string;
  }>({ period: '30' });

  const { data: operations = [], isLoading: historyLoading } = useCryptoHistory(virtualAccountId, filters);
  
  const isLoading = accountLoading || historyLoading;

  // Check if crypto is enabled
  if (!hasCategory('CRYPTO')) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Crypto não habilitado
          </h2>
          <p className="text-surface-400">
            Esta funcionalidade não está disponível para sua conta.
          </p>
        </Card>
      </div>
    );
  }
  
  // Check if user has a virtual account
  if (!accountLoading && !virtualAccountId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h2 className="text-xl font-semibold text-surface-100 mb-2">
            Conta não encontrada
          </h2>
          <p className="text-surface-400">
            Você precisa ter uma conta virtual para ver o histórico crypto.
          </p>
        </Card>
      </div>
    );
  }

  const handleExportCsv = () => {
    // Generate CSV content
    const headers = ['Data', 'Tipo', 'Moeda', 'Quantidade', 'Valor BRL', 'Status', 'TX Hash'];
    const rows = operations.map(op => [
      new Date(op.createdAt).toLocaleString('pt-BR'),
      op.type,
      op.crypto,
      op.amount.toString(),
      (op.valueBrl / 100).toFixed(2),
      op.status,
      op.txHash || '',
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `crypto-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Calculate summary
  const summary = operations.reduce((acc, op) => {
    if (op.type === 'PURCHASE' || op.type === 'DEPOSIT') {
      acc.totalIn += op.valueBrl;
      acc.countIn++;
    } else if (op.type === 'SALE' || op.type === 'WITHDRAW') {
      acc.totalOut += op.valueBrl;
      acc.countOut++;
    }
    return acc;
  }, { totalIn: 0, totalOut: 0, countIn: 0, countOut: 0 });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/banking/crypto')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <History className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100">Histórico Crypto</h1>
            <p className="text-surface-400">Todas as suas operações</p>
          </div>
        </div>
        <Button variant="secondary" onClick={handleExportCsv}>
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-surface-900/50">
          <p className="text-surface-400 text-sm">Total de Operações</p>
          <p className="text-2xl font-bold text-surface-100">{operations.length}</p>
        </Card>
        <Card className="bg-green-900/20 border-green-500/30">
          <p className="text-surface-400 text-sm">Entradas</p>
          <p className="text-2xl font-bold text-green-400">
            +{formatCurrency(summary.totalIn / 100)}
          </p>
          <p className="text-surface-500 text-xs">{summary.countIn} operações</p>
        </Card>
        <Card className="bg-red-900/20 border-red-500/30">
          <p className="text-surface-400 text-sm">Saídas</p>
          <p className="text-2xl font-bold text-red-400">
            -{formatCurrency(summary.totalOut / 100)}
          </p>
          <p className="text-surface-500 text-xs">{summary.countOut} operações</p>
        </Card>
        <Card className="bg-surface-900/50">
          <p className="text-surface-400 text-sm">Saldo Líquido</p>
          <p className={cn(
            'text-2xl font-bold',
            summary.totalIn - summary.totalOut >= 0 ? 'text-green-400' : 'text-red-400'
          )}>
            {formatCurrency((summary.totalIn - summary.totalOut) / 100)}
          </p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-surface-900/50">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Buscar por moeda ou hash..."
              value={filters.search || ''}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            />
          </div>
          <Select
            options={TYPE_OPTIONS}
            value={filters.type || ''}
            onChange={(e) => setFilters(f => ({ ...f, type: e.target.value || undefined }))}
          />
          <Select
            options={STATUS_OPTIONS}
            value={filters.status || ''}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value || undefined }))}
          />
          <Select
            options={PERIOD_OPTIONS}
            value={filters.period || ''}
            onChange={(e) => setFilters(f => ({ ...f, period: e.target.value || undefined }))}
          />
        </div>
      </Card>

      {/* Operations List */}
      {operations.length === 0 ? (
        <Card className="text-center py-12">
          <History className="w-12 h-12 mx-auto mb-4 text-surface-500" />
          <h3 className="text-lg font-medium text-surface-100 mb-2">
            Nenhuma operação encontrada
          </h3>
          <p className="text-surface-400">
            Suas operações crypto aparecerão aqui
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {operations.map((operation) => (
            <OperationRow key={operation.id} operation={operation} />
          ))}
        </div>
      )}
    </div>
  );
}

// OperationRow Component
interface OperationRowProps {
  operation: CryptoOperation;
}

function OperationRow({ operation }: OperationRowProps) {
  const getTypeInfo = (type: string) => {
    switch (type) {
      case 'PURCHASE':
        return { label: 'Compra', icon: ArrowDownLeft, color: 'text-green-400', bgColor: 'bg-green-500/20' };
      case 'SALE':
        return { label: 'Venda', icon: ArrowUpRight, color: 'text-red-400', bgColor: 'bg-red-500/20' };
      case 'DEPOSIT':
        return { label: 'Depósito DeFi', icon: ArrowDownLeft, color: 'text-purple-400', bgColor: 'bg-purple-500/20' };
      case 'WITHDRAW':
        return { label: 'Resgate DeFi', icon: ArrowUpRight, color: 'text-orange-400', bgColor: 'bg-orange-500/20' };
      case 'TRANSFER_IN':
        return { label: 'Recebimento', icon: ArrowDownLeft, color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
      case 'TRANSFER_OUT':
        return { label: 'Envio', icon: ArrowUpRight, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' };
      default:
        return { label: type, icon: RefreshCw, color: 'text-surface-400', bgColor: 'bg-surface-500/20' };
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge className="bg-green-500/20 text-green-400">Concluída</Badge>;
      case 'PENDING':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Pendente</Badge>;
      case 'FAILED':
        return <Badge className="bg-red-500/20 text-red-400">Falha</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const typeInfo = getTypeInfo(operation.type);
  const Icon = typeInfo.icon;
  const isIncoming = ['PURCHASE', 'DEPOSIT', 'TRANSFER_IN'].includes(operation.type);

  return (
    <Card className="hover:border-surface-700 transition-all duration-200">
      <div className="flex items-center gap-4">
        {/* Icon */}
        <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', typeInfo.bgColor)}>
          <Icon className={cn('w-5 h-5', typeInfo.color)} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-surface-100">{typeInfo.label}</span>
            {getStatusBadge(operation.status)}
          </div>
          <div className="flex items-center gap-2 text-sm text-surface-400">
            <span>{operation.crypto}</span>
            <span>•</span>
            <span>{new Date(operation.createdAt).toLocaleString('pt-BR')}</span>
          </div>
        </div>

        {/* Amount */}
        <div className="text-right">
          <p className={cn(
            'font-semibold',
            isIncoming ? 'text-green-400' : 'text-red-400'
          )}>
            {isIncoming ? '+' : '-'}{operation.amount.toFixed(6)} {operation.crypto}
          </p>
          <p className="text-sm text-surface-400">
            {formatCurrency(operation.valueBrl / 100)}
          </p>
        </div>

        {/* TX Hash Link */}
        {operation.txHash && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(`https://etherscan.io/tx/${operation.txHash}`, '_blank')}
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Fees */}
      {operation.fees && operation.fees > 0 && (
        <div className="mt-3 pt-3 border-t border-surface-800 flex justify-between text-sm">
          <span className="text-surface-500">Taxas</span>
          <span className="text-surface-400">{formatCurrency(operation.fees / 100)}</span>
        </div>
      )}
    </Card>
  );
}
